/**
 * Stage 30 · ONE Cyber Asset Intelligence — Security Control Tower.
 * Evidence-only. No opaque scores. No fabricated telemetry.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, AlertTriangle, Search, RefreshCw, Plug, Settings, GitCompare } from "lucide-react";

const STATUS_STYLES = {
  healthy: "bg-emerald-100 text-emerald-800 border-emerald-200",
  attention_required: "bg-amber-100 text-amber-800 border-amber-200",
  high_risk: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
  not_managed: "bg-slate-100 text-slate-500 border-slate-200",
  exempted: "bg-sky-100 text-sky-800 border-sky-200",
};

export default function Security() {
  const [tab, setTab] = useState("dashboard");
  const [mc, setMc] = useState(""); const [masters, setMasters] = useState([]); const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); if (data?.master_company_id) setMc(data.master_company_id); });
    api.get("/master-companies").then(({ data }) => setMasters(data.items || []));
  }, []);
  const isSuntronix = me && !me.master_company_id;
  return (
    <div className="max-w-6xl space-y-5" data-testid="security-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Technology · Security</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">ONE Cyber Asset Intelligence</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          ONE does not replace EDR, MDM or vulnerability scanners. It connects security posture from your approved sources to every asset, employee, transaction and control — with an explainable status for each device.
        </p>
      </div>
      {isSuntronix && (
        <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="sec-tenant">
          <option value="">All tenants (global)</option>
          {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      )}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { key: "dashboard",     label: "Security Cockpit",    icon: Shield },
          { key: "exceptions",    label: "Exceptions",           icon: AlertTriangle },
          { key: "lost",          label: "Lost / Stolen",        icon: AlertTriangle },
          { key: "reconciliation",label: "Cyber Discovery",      icon: GitCompare },
          { key: "connectors",    label: "Connectors",           icon: Plug },
          { key: "config",        label: "Config",               icon: Settings },
          { key: "asset360",      label: "Asset security",       icon: Search },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`sec-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "dashboard"      && <Dashboard mc={mc} />}
      {tab === "exceptions"     && <ExceptionsTab mc={mc} />}
      {tab === "lost"           && <LostStolenTab mc={mc} />}
      {tab === "reconciliation" && <ReconciliationTab />}
      {tab === "connectors"     && <ConnectorsTab isSuntronix={isSuntronix} />}
      {tab === "config"         && <ConfigTab isSuntronix={isSuntronix} />}
      {tab === "asset360"       && <AssetSecurityTab />}
    </div>
  );
}

function StatusChip({ v }) {
  return <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (STATUS_STYLES[v] || STATUS_STYLES.unknown)}>{v || "unknown"}</span>;
}
function Tile({ label, value, tone, testid }) {
  const cls = tone === "critical" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}

/* --------- Dashboard --------- */
function Dashboard({ mc }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/security/dashboard", { params: mc ? { master_company_id: mc } : {} })
       .then(({ data }) => setD(data)).catch(() => setD(null));
  }, [mc]);
  const t = d?.tiles || {};
  return (
    <div className="space-y-3" data-testid="sec-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total assets"           value={t.total_assets} testid="sec-tile-total" />
        <Tile label="Healthy"                value={t.healthy} tone="ok" testid="sec-tile-healthy" />
        <Tile label="Attention required"     value={t.attention_required} tone="warn" testid="sec-tile-attention" />
        <Tile label="High risk"              value={t.high_risk} tone="warn" testid="sec-tile-highrisk" />
        <Tile label="Critical"               value={t.critical} tone="critical" testid="sec-tile-critical" />
        <Tile label="Unknown / Unmanaged"    value={t.unknown_or_unmanaged} testid="sec-tile-unknown" />
        <Tile label="MDM missing"            value={t.mdm_missing} tone="warn" />
        <Tile label="Endpoint agent missing" value={t.endpoint_agent_missing} tone="warn" />
        <Tile label="Stale sync data"        value={t.stale_security_data} tone="warn" />
        <Tile label="Lost / Stolen open"     value={t.lost_stolen_open} tone="critical" />
        <Tile label="Exceptions open"        value={t.exceptions_open} tone="warn" testid="sec-tile-exc" />
        <Tile label="Security hold"          value={t.security_hold} tone="warn" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">
        All figures resolve to real assets and events — click any tile in the exceptions or lost/stolen tabs to drill.
      </div>
    </div>
  );
}

/* --------- Exceptions --------- */
function ExceptionsTab({ mc }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const load = useCallback(() => api.get("/security/exceptions", { params: { status, master_company_id: mc || undefined } })
    .then(({ data }) => setItems(data.items || [])).catch(() => setItems([])), [mc, status]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (id) => {
    try { await api.post(`/security/exceptions/${id}/resolve?remediation=resolved+via+cockpit`); toast.success("Resolved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="sec-exceptions">
      <div className="flex gap-1">
        {["open", "resolved"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={"px-3 py-1.5 rounded-md text-xs border " + (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700")}>
            {s}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Severity</th><th className="text-left py-2 px-3">Asset / Employee</th><th className="text-left py-2 px-3">Reason</th><th className="text-left py-2 px-3">Detected</th><th className="text-right py-2 px-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No {status} exceptions.</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{e.kind}</td>
                <td className="py-2 px-3"><StatusChip v={e.severity} /></td>
                <td className="py-2 px-3 text-slate-700 text-xs font-mono">{(e.asset_id || e.employee_id || "").slice(0, 8)}</td>
                <td className="py-2 px-3 text-slate-800">{e.reason}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{e.detected_at?.slice(0, 10)}</td>
                <td className="py-2 px-3 text-right">
                  {status === "open" && <button onClick={() => resolve(e.id)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`sec-exc-resolve-${e.id}`}>Resolve</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------- Lost / Stolen --------- */
function LostStolenTab({ mc }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/security/lost-stolen", { params: mc ? { master_company_id: mc } : {} })
    .then(({ data }) => setItems(data.items || [])).catch(() => setItems([])), [mc]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="sec-lost">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="sec-lost-new">Report lost / stolen</button>
      </div>
      {showForm && <LostForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Asset</th><th className="text-left py-2 px-3">Incident</th><th className="text-left py-2 px-3">Last known</th><th className="text-left py-2 px-3">Police ref</th><th className="text-left py-2 px-3">Recovery</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No lost/stolen reports.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900 text-xs">{r.asset_id?.slice(0, 8)}</td>
                <td className="py-2 px-3 text-slate-700 text-xs">{r.incident_date?.slice(0, 10)}</td>
                <td className="py-2 px-3 text-slate-800">{r.last_known_location || "—"}</td>
                <td className="py-2 px-3 text-slate-700 text-xs">{r.police_reference || "—"}</td>
                <td className="py-2 px-3"><StatusChip v={r.recovery_status || "reported"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function LostForm({ onCancel, onCreated }) {
  const [f, setF] = useState({ asset_id: "", incident_date: new Date().toISOString().slice(0, 10), description: "", last_known_location: "", police_reference: "" });
  const submit = async () => {
    try {
      await api.post("/security/lost-stolen", f);
      toast.success("Lost/stolen reported — critical exception raised");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Asset ID</div><input className="input" value={f.asset_id} onChange={(e) => setF({ ...f, asset_id: e.target.value })} data-testid="sec-lost-asset" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Incident date</div><input type="date" className="input" value={f.incident_date} onChange={(e) => setF({ ...f, incident_date: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div><textarea className="input min-h-[60px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sec-lost-desc" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Last known location</div><input className="input" value={f.last_known_location} onChange={(e) => setF({ ...f, last_known_location: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Police reference</div><input className="input" value={f.police_reference} onChange={(e) => setF({ ...f, police_reference: e.target.value })} /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
        <button onClick={submit} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="sec-lost-save">Save report</button>
      </div>
    </div>
  );
}

/* --------- Reconciliation --------- */
function ReconciliationTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/security/reconciliation").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  const s = d?.summary || {};
  return (
    <div className="space-y-3" data-testid="sec-recon">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Known · with security"      value={s.known_with_security} tone="ok" />
        <Tile label="Known · missing from security" value={s.known_missing_from_security} tone="warn" />
        <Tile label="Unknown in security tool"   value={s.unknown_devices_in_security} tone="warn" />
        <Tile label="Duplicate identities"        value={s.duplicate_security_identities} tone="warn" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">Reconciliation queue lists real serials from both sides — no synthetic entries.</div>
    </div>
  );
}

/* --------- Connectors --------- */
function ConnectorsTab({ isSuntronix }) {
  const [items, setItems] = useState([]); const [kinds, setKinds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/security/connectors").then(({ data }) => { setItems(data.items || []); setKinds(data.supported_kinds || []); }).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="sec-connectors">
      <div className="flex justify-end">
        {isSuntronix && <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="sec-connector-new">Add connector</button>}
      </div>
      {showForm && <ConnectorForm kinds={kinds} onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Endpoint</th><th className="text-left py-2 px-3">Status</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="py-6 text-center text-slate-500">No connectors configured. ONE will only display CONNECTED once a real integration is validated.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-900">{c.name}</td>
                <td className="py-2 px-3 font-mono text-slate-700">{c.kind}</td>
                <td className="py-2 px-3 text-slate-600 text-xs">{c.endpoint_url || "—"}</td>
                <td className="py-2 px-3"><StatusChip v={c.connected ? "healthy" : "not_managed"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function ConnectorForm({ kinds, onCancel, onSaved }) {
  const [f, setF] = useState({ name: "", kind: kinds[0] || "intune", endpoint_url: "", connected: false });
  const save = async () => {
    try { await api.post("/security/connectors", f); toast.success("Connector saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div><select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{kinds.map((k) => <option key={k}>{k}</option>)}</select></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Endpoint URL</div><input className="input" value={f.endpoint_url} onChange={(e) => setF({ ...f, endpoint_url: e.target.value })} placeholder="https://…" /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.connected} onChange={(e) => setF({ ...f, connected: e.target.checked })} /><span className="text-sm">Mark as connected (requires endpoint)</span></label>
      </div>
      <div className="flex justify-end gap-2 pt-1"><button onClick={onCancel} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Cancel</button><button onClick={save} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white">Save</button></div>
    </div>
  );
}

/* --------- Config --------- */
function ConfigTab({ isSuntronix }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/security/config").then(({ data }) => setC(data)); }, []);
  if (!c) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="sec-config">
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
        <Row label="Statuses">{c.statuses.map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 mr-1">{s}</span>)}</Row>
        <Row label="Exception types">{c.exception_types.map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 mr-1 font-mono">{s}</span>)}</Row>
        <Row label="Required controls">{(c.required_controls || []).join(", ")}</Row>
        <Row label="Freshness (days)">{c.freshness_days}</Row>
      </div>
      {isSuntronix && <div className="text-[10px] uppercase tracking-widest text-slate-500">To edit thresholds, source priority, and required controls — post JSON to /api/security/config (super_admin only). UI editor is a follow-up.</div>}
    </div>
  );
}
function Row({ label, children }) {
  return <div className="grid grid-cols-4 gap-3"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 pt-0.5">{label}</div><div className="col-span-3 text-sm text-slate-800">{children}</div></div>;
}

/* --------- Asset Security --------- */
function AssetSecurityTab() {
  const [assetId, setAssetId] = useState("");
  const [p, setP] = useState(null);
  const load = async () => {
    if (!assetId.trim()) { toast.error("Enter an asset id"); return; }
    try { const { data } = await api.get(`/security/profile/${assetId.trim()}`); setP(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Not found")); }
  };
  return (
    <div className="space-y-4" data-testid="sec-asset360">
      <div className="flex items-end gap-2">
        <label className="flex-1"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Asset ID</div><input className="input w-full" value={assetId} onChange={(e) => setAssetId(e.target.value)} placeholder="paste an asset id" data-testid="sec-asset-input" /></label>
        <button onClick={load} className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm inline-flex items-center gap-1" data-testid="sec-asset-lookup"><RefreshCw className="h-4 w-4" /> View security</button>
      </div>
      {p && (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Asset</div>
                <div className="text-sm font-medium">{p.asset.asset_tag || p.asset.serial || p.asset.id.slice(0, 8)} · {p.asset.model || "—"}</div>
              </div>
              <div className="ml-auto"><StatusChip v={p.profile?.security_status || "unknown"} /></div>
            </div>
            {p.profile?.status_reason && <div className="mt-2 text-sm text-slate-800">{p.profile.status_reason}</div>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Tile label="MDM enrolled"   value={fmtBool(p.profile?.mdm_enrollment)} />
              <Tile label="MDM provider"   value={p.profile?.mdm_provider || "—"} />
              <Tile label="Encryption"     value={p.profile?.encryption_status || "—"} />
              <Tile label="Agent status"   value={p.profile?.endpoint_agent_status || "—"} />
              <Tile label="OS"             value={`${p.profile?.os_name || ""} ${p.profile?.os_version || ""}`.trim() || "—"} />
              <Tile label="Firewall"       value={p.profile?.firewall_status || "—"} />
              <Tile label="Vuln · critical" value={p.profile?.vulnerabilities?.critical ?? "—"} tone={p.profile?.vulnerabilities?.critical ? "critical" : undefined} />
              <Tile label="Last sync"      value={p.profile?.last_security_sync?.slice(0, 16).replace("T", " ") || "—"} />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="p-3 border-b border-slate-200 text-sm font-medium">Security history ({p.history?.length || 0})</div>
            {(p.history || []).length === 0 ? <div className="p-6 text-sm text-slate-500">No security events recorded.</div> : (
              <ul className="divide-y divide-slate-100">
                {p.history.map((h) => (
                  <li key={h.id} className="p-3 text-sm">
                    <span className="text-[10px] font-mono text-slate-500">{h.at?.slice(0, 16).replace("T", " ")}</span> ·
                    <StatusChip v={h.security_status} /> ·
                    <span className="text-slate-700 ml-1">{h.status_reason || "-"}</span>
                    <span className="text-[10px] text-slate-500 ml-2">source {h.source}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
function fmtBool(v) { return v === true ? "yes" : v === false ? "no" : "—"; }
